import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Lightning, Rocket, Eye, ShoppingCart, CurrencyDollar,
  Play, Lock, Users, TrendUp, VideoCamera, Star,
  ArrowRight, GoogleLogo, Check, Fire, Broadcast
} from '@phosphor-icons/react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_FILTERS = ['All', 'Tech', 'Finance', 'Gaming', 'Health', 'Education', 'E-Commerce', 'SaaS'];

const CATEGORY_MAP = {
  'Tech': ['SaaS', 'Technology'],
  'Finance': ['Finance'],
  'Gaming': ['Gaming'],
  'Health': ['Health'],
  'Education': ['Education'],
  'E-Commerce': ['E-Commerce'],
  'SaaS': ['SaaS'],
};

const HOW_IT_WORKS = [
  { step: 1, title: 'Discover', desc: 'Browse AI-curated hot products and trending offers', icon: Eye, color: 'from-blue-500 to-cyan-400' },
  { step: 2, title: 'Watch', desc: 'See creator reviews, live streams and shoppable videos', icon: VideoCamera, color: 'from-purple-500 to-pink-400' },
  { step: 3, title: 'Sign Up', desc: 'Join free and unlock your personal affiliate dashboard', icon: Rocket, color: 'from-emerald-500 to-teal-400' },
  { step: 4, title: 'Buy', desc: 'Shop through smart links with exclusive deals', icon: ShoppingCart, color: 'from-amber-500 to-orange-400' },
  { step: 5, title: 'Earn', desc: 'Earn commissions on every conversion you drive', icon: CurrencyDollar, color: 'from-green-500 to-emerald-400' },
];

function handleGoogleLogin() {
  var redirectUrl = window.location.origin + '/';
  window.location.href = 'https://auth.emergentagent.com/?redirect=' + encodeURIComponent(redirectUrl);
}

export default function LandingPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [category, setCategory] = useState('All');
  const [authModal, setAuthModal] = useState(null);
  const [authTab, setAuthTab] = useState('signup');
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'affiliate' });
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const tickerRef = useRef(null);

  useEffect(() => {
    if (user) {
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'advertiser') navigate('/advertiser');
      else navigate('/affiliate');
    }
  }, [user, navigate]);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API_BASE}/api/landing/hot-products`);
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
    }
    load();
  }, []);

  const filteredOffers = data?.offers?.filter(o => {
    if (category === 'All') return true;
    const cats = CATEGORY_MAP[category] || [category];
    return cats.some(c => o.category?.toLowerCase() === c.toLowerCase());
  })?.slice(0, 6) || [];

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    try {
      if (authTab === 'login') {
        const result = await login(form.email, form.password);
        if (result && !result.success) {
          setAuthError(result.error || 'Login failed');
          setAuthLoading(false);
          return;
        }
        toast.success('Welcome back!');
        setAuthModal(null);
        window.location.href = '/dashboard';
        return;
      } else {
        const res = await fetch(`${API_BASE}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: form.email, password: form.password, name: form.name, role: form.role }),
        });
        const d = await res.json();
        if (!res.ok) throw new Error(d.detail || 'Registration failed');
        localStorage.setItem('dw_token', d.token);
        toast.success('Account created! Welcome to Dynamic World');
        window.location.href = '/dashboard';
        return;
      }
    } catch (err) {
      setAuthError(err.response?.data?.detail || err.message || 'Connection error. Please check your internet and try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const tickerItems = [
    'AI Smart Link generated $120 commission on VPN signup',
    'Flash Deal: 40% OFF Crypto Trading Platform',
    'Alex Thompson earned $2,450 this week',
    'New offer: EdTech Course Platform - $55 CPA',
    'Live stream: Flash Sale Friday - 24 viewers',
    'Sarah Chen hit 500 conversions milestone',
    'Premium VPN Signup trending - $45/conversion',
    'Creator Pro plan: Unlimited video uploads',
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 overflow-x-hidden" data-testid="landing-page">

      {/* ===== STICKY NAV ===== */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-800/50" data-testid="sticky-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center font-bold text-sm">DW</div>
            <span className="font-bold text-lg tracking-tight hidden sm:block">Dynamic World</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm text-zinc-400">
            <a href="#products" className="hover:text-white transition-colors">Products</a>
            <a href="#creators" className="hover:text-white transition-colors">Creators</a>
            <a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => { setAuthTab('login'); setAuthModal(true); }} data-testid="nav-login-btn" className="text-zinc-300 hover:text-white text-sm">
              Log In
            </Button>
            <Button onClick={() => { setAuthTab('signup'); setAuthModal(true); }} data-testid="nav-signup-btn" className="bg-blue-600 hover:bg-blue-700 text-sm px-4">
              Sign Up Free
            </Button>
          </div>
        </div>
      </nav>

      {/* ===== HERO ===== */}
      <section className="relative pt-28 pb-20 sm:pt-36 sm:pb-28 px-4 overflow-hidden" data-testid="hero-section">
        {/* Animated orbs */}
        <div className="absolute top-20 left-1/4 w-[500px] h-[500px] rounded-full bg-blue-600/8 blur-[120px] animate-pulse" />
        <div className="absolute bottom-10 right-1/4 w-[400px] h-[400px] rounded-full bg-purple-600/8 blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full bg-emerald-500/5 blur-[80px] animate-pulse" style={{ animationDelay: '2s' }} />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-600/10 border border-blue-500/20 text-blue-400 text-xs font-medium mb-6">
            <Lightning size={14} weight="fill" /> AI-Powered Affiliate Commerce
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]">
            Discover. Promote.{' '}
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-emerald-400 bg-clip-text text-transparent">
              Earn.
            </span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 mt-5 max-w-2xl mx-auto leading-relaxed">
            The global creator-commerce platform where AI finds the hottest products, smart links track every click, and creators earn real commissions.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
            <Button onClick={() => { setAuthTab('signup'); setAuthModal(true); }} data-testid="hero-signup-btn" className="bg-blue-600 hover:bg-blue-700 h-12 px-8 text-base font-semibold rounded-xl w-full sm:w-auto">
              Start Earning Free <ArrowRight size={18} className="ml-2" />
            </Button>
            <Button variant="outline" onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-12 px-8 text-base rounded-xl w-full sm:w-auto">
              Browse Products
            </Button>
          </div>

          {/* Social Proof Stats */}
          <div className="flex items-center justify-center gap-6 sm:gap-10 mt-12">
            {[
              { value: `${((data?.stats?.total_affiliates || 0) / 1000).toFixed(0)}K+`, label: 'Members' },
              { value: `$${((data?.stats?.total_revenue || 0) / 1000).toFixed(1)}K`, label: 'Earned' },
              { value: `${data?.stats?.total_offers || 0}+`, label: 'Offers' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-white to-zinc-400 bg-clip-text text-transparent">{s.value}</p>
                <p className="text-[11px] text-zinc-500 uppercase tracking-wider mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== LIVE TICKER ===== */}
      <div className="border-y border-zinc-800/50 bg-zinc-900/30 py-3 overflow-hidden" data-testid="live-ticker">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...tickerItems, ...tickerItems].map((item, i) => (
            <span key={i} className="mx-8 text-sm text-zinc-500 flex items-center gap-2">
              <Fire size={14} className="text-amber-500 shrink-0" /> {item}
            </span>
          ))}
        </div>
      </div>

      {/* ===== HOT PRODUCTS ===== */}
      <section id="products" className="py-16 sm:py-20 px-4" data-testid="hot-products-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold">Hot Products</h2>
            <p className="text-sm text-zinc-500 mt-2">AI-curated high-converting offers updated in real-time</p>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="category-filters">
            {CATEGORY_FILTERS.map(cat => (
              <button key={cat} onClick={() => setCategory(cat)} data-testid={`filter-${cat}`}
                className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${
                  category === cat
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                    : 'bg-zinc-800/60 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 border border-zinc-700/50'
                }`}>
                {cat}
              </button>
            ))}
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(filteredOffers.length > 0 ? filteredOffers : data?.offers?.slice(0, 6) || []).map((offer, i) => (
              <div key={offer.offer_id || i} className="group relative bg-zinc-900/60 border border-zinc-800/60 rounded-2xl overflow-hidden hover:border-zinc-700/60 transition-all duration-300" data-testid={`product-card-${i}`}>
                {/* Header gradient */}
                <div className={`h-2 bg-gradient-to-r ${
                  ['from-blue-500 to-cyan-400', 'from-purple-500 to-pink-400', 'from-emerald-500 to-teal-400',
                   'from-amber-500 to-orange-400', 'from-red-500 to-rose-400', 'from-indigo-500 to-violet-400'][i % 6]
                }`} />
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{offer.category}</span>
                      <h3 className="text-base font-semibold text-zinc-100 mt-1 truncate">{offer.title}</h3>
                    </div>
                    <span className="shrink-0 ml-3 px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20">
                      ${offer.payout}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-2 line-clamp-2">{offer.description}</p>
                  <div className="flex items-center gap-3 mt-3 text-[10px] text-zinc-600">
                    <span className="flex items-center gap-1"><TrendUp size={12} /> {offer.total_conversions || 0} conv.</span>
                    <span className="px-1.5 py-0.5 rounded bg-zinc-800 border border-zinc-700">{offer.type}</span>
                    {offer.geo_targets?.slice(0, 3).map(g => (
                      <span key={g} className="px-1.5 py-0.5 rounded bg-zinc-800 border border-zinc-700">{g}</span>
                    ))}
                  </div>
                  <button
                    onClick={() => { setAuthTab('signup'); setAuthModal(true); }}
                    className="mt-4 w-full py-2 rounded-lg bg-blue-600/10 text-blue-400 text-xs font-medium border border-blue-500/20 hover:bg-blue-600/20 transition-colors"
                    data-testid={`product-cta-${i}`}
                  >
                    Sign Up to Promote &rarr;
                  </button>
                </div>
                {/* Lock overlay on hover */}
                <div className="absolute inset-0 bg-zinc-950/80 backdrop-blur-sm flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer"
                  onClick={() => { setAuthTab('signup'); setAuthModal(true); }}>
                  <Lock size={28} className="text-blue-400 mb-2" />
                  <p className="text-sm font-semibold text-white">Sign Up to Promote</p>
                  <p className="text-[11px] text-zinc-400 mt-1">Earn ${offer.payout} per conversion</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CREATOR VIDEOS ===== */}
      <section id="creators" className="py-16 sm:py-20 px-4 bg-zinc-900/20" data-testid="creator-videos-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold">Creator Videos</h2>
            <p className="text-sm text-zinc-500 mt-2">Watch product reviews and shoppable content from top creators</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(data?.creators || []).slice(0, 3).map((creator, i) => (
              <div key={creator.profile_id || i} className="group relative bg-zinc-900/60 border border-zinc-800/60 rounded-2xl overflow-hidden" data-testid={`creator-video-${i}`}>
                <div className="aspect-[9/12] bg-gradient-to-b from-zinc-800 to-zinc-900 flex items-center justify-center relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                      <Play size={28} weight="fill" className="text-white ml-1" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-sm font-bold text-white">
                        {creator.display_name?.[0] || 'C'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{creator.display_name}</p>
                        <p className="text-[10px] text-zinc-400"><Users size={10} className="inline mr-0.5" />{creator.subscriber_count || 0} followers</p>
                      </div>
                    </div>
                    <div className="flex gap-1.5 mt-2">
                      {creator.categories?.slice(0, 2).map(c => (
                        <span key={c} className="px-2 py-0.5 rounded-full text-[10px] bg-white/10 text-zinc-300 border border-white/10">{c}</span>
                      ))}
                    </div>
                  </div>
                  {/* Members-only gate */}
                  <div className="absolute inset-0 bg-zinc-950/70 backdrop-blur-[2px] flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer"
                    onClick={() => { setAuthTab('signup'); setAuthModal(true); }}>
                    <Lock size={24} className="text-purple-400 mb-2" />
                    <p className="text-sm font-semibold text-white">Members Only</p>
                    <Button size="sm" className="mt-3 bg-purple-600 hover:bg-purple-700 text-xs rounded-full px-5">
                      Sign Up to Watch
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {(data?.creators || []).length === 0 && [0, 1, 2].map(i => (
              <div key={i} className="group relative bg-zinc-900/60 border border-zinc-800/60 rounded-2xl overflow-hidden">
                <div className="aspect-[9/12] bg-gradient-to-b from-zinc-800 to-zinc-900 flex items-center justify-center">
                  <div className="text-center">
                    <VideoCamera size={32} className="text-zinc-700 mx-auto mb-2" />
                    <p className="text-xs text-zinc-600">Creator content coming soon</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="py-16 sm:py-20 px-4" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold">How It Works</h2>
            <p className="text-sm text-zinc-500 mt-2">From discovery to earnings in 5 simple steps</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {HOW_IT_WORKS.map((step) => {
              var Icon = step.icon;
              return (
                <div key={step.step} className="text-center group" data-testid={`step-${step.step}`}>
                  <div className={`w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <Icon size={28} weight="duotone" className="text-white" />
                  </div>
                  <div className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Step {step.step}</div>
                  <h3 className="text-base font-semibold">{step.title}</h3>
                  <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== CTA FOOTER ===== */}
      <section className="py-16 sm:py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-purple-600/5 to-emerald-600/5" />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl font-bold">Ready to Start Earning?</h2>
          <p className="text-sm text-zinc-400 mt-3">Join thousands of creators and affiliates already monetizing with Dynamic World</p>
          <Button onClick={() => { setAuthTab('signup'); setAuthModal(true); }} className="bg-blue-600 hover:bg-blue-700 h-12 px-8 text-base font-semibold rounded-xl mt-6">
            Create Free Account <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-zinc-800/50 py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-zinc-600">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-600 rounded-lg flex items-center justify-center font-bold text-[10px]">DW</div>
            <span>Dynamic World Ltd (United Kingdom)</span>
          </div>
          <div className="flex items-center gap-4">
            <span>Creator Commerce Platform</span>
            <span>&middot;</span>
            <span>v3.0</span>
          </div>
        </div>
      </footer>

      {/* ===== AUTH MODAL ===== */}
      {authModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="auth-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md shadow-2xl shadow-black/50">
            <div className="p-6">
              {/* Tabs */}
              <div className="flex gap-1 bg-zinc-800/50 rounded-xl p-1 mb-6">
                <button onClick={() => { setAuthTab('signup'); setAuthError(''); }} data-testid="auth-tab-signup"
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${authTab === 'signup' ? 'bg-blue-600 text-white shadow-lg' : 'text-zinc-400 hover:text-zinc-200'}`}>
                  Sign Up
                </button>
                <button onClick={() => { setAuthTab('login'); setAuthError(''); }} data-testid="auth-tab-login"
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${authTab === 'login' ? 'bg-blue-600 text-white shadow-lg' : 'text-zinc-400 hover:text-zinc-200'}`}>
                  Log In
                </button>
              </div>

              <form onSubmit={handleAuth} className="space-y-4">
                {authError && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">{authError}</div>
                )}
                {authTab === 'signup' && (
                  <Input data-testid="auth-name" value={form.name} onChange={(e) => setForm(p => ({...p, name: e.target.value}))} placeholder="Full name" className="bg-zinc-800 border-zinc-700 h-11 rounded-xl" required />
                )}
                <Input data-testid="auth-email" type="email" value={form.email} onChange={(e) => setForm(p => ({...p, email: e.target.value}))} placeholder="Email address" className="bg-zinc-800 border-zinc-700 h-11 rounded-xl" required />
                <Input data-testid="auth-password" type="password" value={form.password} onChange={(e) => setForm(p => ({...p, password: e.target.value}))} placeholder="Password" className="bg-zinc-800 border-zinc-700 h-11 rounded-xl" required />
                {authTab === 'login' && (
                  <div className="flex justify-end -mt-2">
                    <a href="/login" onClick={(e) => { e.preventDefault(); setAuthModal(null); window.location.href = '/login'; }}
                      className="text-xs text-blue-400 hover:text-blue-300 transition-colors" data-testid="modal-forgot-link">
                      Forgot password?
                    </a>
                  </div>
                )}
                {authTab === 'signup' && (
                  <div className="flex gap-2">
                    {['affiliate', 'advertiser'].map(r => (
                      <button key={r} type="button" onClick={() => setForm(p => ({...p, role: r}))}
                        className={`flex-1 py-2.5 rounded-xl text-xs font-medium border transition-all ${form.role === r ? 'bg-blue-600/10 border-blue-500/30 text-blue-400' : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'}`}>
                        {r === 'affiliate' ? 'Creator / Affiliate' : 'Advertiser / Brand'}
                      </button>
                    ))}
                  </div>
                )}
                <Button type="submit" disabled={authLoading} data-testid="auth-submit-btn" className="w-full bg-blue-600 hover:bg-blue-700 h-11 text-sm font-semibold rounded-xl">
                  {authLoading ? 'Processing...' : authTab === 'signup' ? 'Create Account' : 'Sign In'}
                </Button>
              </form>

              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-800" /></div>
                <div className="relative flex justify-center text-xs"><span className="bg-zinc-900 px-3 text-zinc-500">or continue with</span></div>
              </div>

              <Button variant="outline" onClick={handleGoogleLogin} data-testid="google-auth-btn" className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-11 rounded-xl">
                <GoogleLogo size={18} className="mr-2" /> Google
              </Button>
            </div>

            <button onClick={() => setAuthModal(null)} className="w-full py-3 border-t border-zinc-800 text-sm text-zinc-500 hover:text-zinc-300 transition-colors">
              Close
            </button>
          </div>
        </div>
      )}

      {/* ===== MARQUEE CSS ===== */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
      `}</style>
    </div>
  );
}
